import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useTenant } from "../../../../hooks/useTenant";
import SwimSetLibraryService from "../../../../services/swim_set_library_service";
import StrengthExerciseLibraryService from "../../../../services/strength_exercise_library_service";
import WodBenchmarkLibraryService from "../../../../services/wod_benchmark_library_service";
import RunningSessionLibraryService from "../../../../services/running_session_library_service";
import { buildStrengthWorkoutSetFromExercise, getStrengthExerciseById } from "../domain/strength/exerciseLibrary";

export const useBlockLibrary = ({ namePrefix, setFieldValue, onLibraryChanged } = {}) => {
    const { idTenant, user: authUser } = useTenant();

    const handleSaveSwimSet = async (set) => {
        const defaultName = `${set.reps || 1}x${set.distance || ""} ${set.style || "Livre"}`.trim();

        const { value: name } = await Swal.fire({
            title: "Salvar na Biblioteca",
            text: "Digite um nome para identificar este set na sua biblioteca:",
            input: "text",
            inputValue: defaultName,
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#2196f3",
            cancelButtonColor: "#6c757d",
            inputValidator: (value) => {
                if (!value || !value.trim()) return "Você precisa digitar um nome!";
            }
        });

        if (!name) return;

        try {
            await SwimSetLibraryService.save({
                idTenant,
                authUser,
                name: name.trim(),
                category: set.setType || "Personalizado",
                set,
            });
            onLibraryChanged?.();
            toast.success("Set salvo na biblioteca!");
        } catch (error) {
            toast.error(error.message || "Erro ao salvar set na biblioteca.");
        }
    };

    const handleSaveStrengthExercise = async (set) => {
        const defaultName = set.exercise || "";

        const { value: name } = await Swal.fire({
            title: "Salvar na Biblioteca",
            text: "Digite um nome para identificar este exercício na sua biblioteca:",
            input: "text",
            inputValue: defaultName,
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#FF5722",
            cancelButtonColor: "#6c757d",
            inputValidator: (value) => {
                if (!value || !value.trim()) return "Você precisa digitar um nome!";
            }
        });

        if (!name) return;

        try {
            await StrengthExerciseLibraryService.save({
                idTenant,
                authUser,
                name: name.trim(),
                category: set.muscleGroup || "Personalizado",
                exercise: {
                    name: name.trim(),
                    category: set.muscleGroup || "Personalizado",
                    primaryMuscles: set.primaryMuscles?.length ? set.primaryMuscles : (set.muscleGroup ? [set.muscleGroup] : []),
                    secondaryMuscles: set.secondaryMuscles || [],
                    equipment: set.equipment ? [set.equipment] : [],
                    difficulty: set.difficulty || "Iniciante",
                    instructions: set.instructions || set.description || "",
                    tags: set.tags || [],
                }
            });
            onLibraryChanged?.();
            toast.success("Exercício salvo na biblioteca!");
        } catch (error) {
            toast.error(error.message || "Erro ao salvar exercício na biblioteca.");
        }
    };

    const handleSaveWodBenchmark = async (block) => {
        const defaultName = block.name || "WOD Personalizado";

        const { value: formValues } = await Swal.fire({
            title: "Salvar WOD como Benchmark",
            html: `
                <div class="text-start mb-3">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Nome do WOD</label>
                    <input id="swal-input-name" class="form-control" placeholder="Ex: Helen, Amanda" value="${defaultName}">
                </div>
                <div class="text-start mb-3">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Grupo do WOD</label>
                    <select id="swal-input-group" class="form-select font-size-12">
                        <option value="box">Treinos da Box (Padrão)</option>
                        <option value="girls">Girls</option>
                        <option value="heroes">Heroes</option>
                        <option value="open">Open</option>
                        <option value="engine">Engine</option>
                        <option value="strength">Strength</option>
                    </select>
                </div>
                <div class="text-start">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Dicas/Estímulo (Opcional)</label>
                    <textarea id="swal-input-stimulus" class="form-control font-size-12" rows="2" placeholder="Descreva a intenção ou dicas para o WOD...">${block.stimulus || ""}</textarea>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#f59e0b",
            cancelButtonColor: "#6c757d",
            preConfirm: () => {
                const name = document.getElementById("swal-input-name").value.trim();
                const group = document.getElementById("swal-input-group").value;
                const stimulus = document.getElementById("swal-input-stimulus").value.trim();
                if (!name) {
                    Swal.showValidationMessage("O nome do WOD é obrigatório!");
                    return false;
                }
                return { name, group, stimulus };
            }
        });

        if (!formValues) return;
        const { name, group, stimulus } = formValues;

        try {
            await WodBenchmarkLibraryService.save({
                idTenant,
                authUser,
                benchmark: {
                    name,
                    group,
                    track: "crossfit",
                    format: block.format || "amrap",
                    scoreType: block.scoreType || "completion",
                    timeCap: block.timeCap || "",
                    rounds: block.rounds || "",
                    stimulus: stimulus || block.stimulus || "",
                    equipment: [],
                    tags: ["box"],
                    blocks: [{ ...block, name, stimulus: stimulus || block.stimulus || "" }]
                }
            });
            onLibraryChanged?.();
            toast.success("WOD salvo na biblioteca de benchmarks!");
        } catch (error) {
            toast.error(error.message || "Erro ao salvar WOD.");
        }
    };

    const handleSaveRunningSession = async (block) => {
        const defaultName = block.name || "Sessão de corrida";

        const { value: formValues } = await Swal.fire({
            title: "Salvar sessão de corrida",
            html: `
                <div class="text-start mb-3">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Nome da sessão</label>
                    <input id="running-session-name" class="form-control" placeholder="Ex: 6x400m ritmo 5K" value="${defaultName}">
                </div>
                <div class="text-start mb-3">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Tags</label>
                    <input id="running-session-tags" class="form-control" placeholder="pista, 5k, tiros" value="clube">
                </div>
                <div class="text-start">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Observações</label>
                    <textarea id="running-session-notes" class="form-control font-size-12" rows="2" placeholder="Foco técnico, logística ou variação por grupos.">${block.description || ""}</textarea>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#2f9e44",
            cancelButtonColor: "#6c757d",
            preConfirm: () => {
                const name = document.getElementById("running-session-name").value.trim();
                if (!name) {
                    Swal.showValidationMessage("O nome da sessão é obrigatório.");
                    return false;
                }
                return {
                    name,
                    tags: document.getElementById("running-session-tags").value.split(",").map(tag => tag.trim()).filter(Boolean),
                    coachNotes: document.getElementById("running-session-notes").value.trim(),
                };
            }
        });

        if (!formValues) return;

        try {
            await RunningSessionLibraryService.save({
                idTenant,
                authUser,
                session: {
                    name: formValues.name,
                    group: "club",
                    sessionType: block.sessionType || "easy",
                    locationType: block.locationType || "street",
                    terrain: block.terrain || "flat",
                    tags: formValues.tags,
                    coachNotes: formValues.coachNotes,
                    blocks: [{ ...block, name: formValues.name, workoutType: "running" }],
                },
            });
            onLibraryChanged?.();
            toast.success("Sessão salva na biblioteca de corrida!");
        } catch (error) {
            toast.error(error.message || "Erro ao salvar sessão de corrida.");
        }
    };

    const replaceStrengthExercise = (blockIndex, setIndex, currentSet, exerciseId) => {
        const exercise = getStrengthExerciseById(exerciseId);
        if (!exercise || !setFieldValue) {
            toast.error("Alternativa não encontrada na biblioteca.");
            return;
        }

        setFieldValue(
            `${namePrefix}.${blockIndex}.sets.${setIndex}`,
            buildStrengthWorkoutSetFromExercise(exercise, {
                id: currentSet.id || Date.now().toString(),
                strengthMethod: currentSet.strengthMethod || "straight",
                sets: currentSet.sets || 3,
                reps: currentSet.reps || "12",
                load: currentSet.load || "",
                rest: currentSet.rest || "60s",
                tempo: currentSet.tempo || "",
            })
        );
    };

    const duplicateSet = (setArrayHelpers, setIndex, set) => {
        setArrayHelpers.insert(setIndex + 1, {
            ...set,
            id: Date.now().toString(),
            exercise: set.exercise ? `${set.exercise} (cópia)` : "",
        });
    };

    const duplicateBlock = (push, block, blockIndex) => {
        push({
            ...block,
            id: Date.now().toString(),
            name: `${block.name || `Bloco ${blockIndex + 1}`} (cópia)`,
            sets: (block.sets || []).map((set, setIndex) => ({
                ...set,
                id: `${Date.now()}_${setIndex}`,
            })),
        });
    };

    return {
        handleSaveSwimSet,
        handleSaveStrengthExercise,
        handleSaveWodBenchmark,
        handleSaveRunningSession,
        replaceStrengthExercise,
        duplicateSet,
        duplicateBlock,
    };
};
